// WyldTyle - Letter placement and trade selection logic

let selectedBankIndex = -1;
let selectedBankLetter = '';
let pendingWildPlacement = null; // {wordIndex, slotIndex} when placing a wild

// --- Capacitor Native Integration ---

function isCapacitor() {
    return window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
}

function haptic(style) {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.Haptics) return;
    const Haptics = Capacitor.Plugins.Haptics;
    if (style === 'light') {
        Haptics.impact({ style: 'LIGHT' });
    } else if (style === 'medium') {
        Haptics.impact({ style: 'MEDIUM' });
    } else if (style === 'success') {
        Haptics.notification({ type: 'SUCCESS' });
    }
}

// --- Sound ---
//
// Short effects from web/static/sfx (made by tools/sfx/sfx.py), played
// through Web Audio so they start without lag and several can overlap.
// They load on game pages only, and play once the player has touched the
// page (browsers keep audio locked until then). The audio session is
// "ambient": sounds mix with the player's own music instead of stopping
// it, and on iPhone the silent switch mutes them. The speaker button in
// the header turns them off, remembered per device.
const SFX_NAMES = ['tap', 'select', 'roll', 'place', 'undo', 'word', 'invalid', 'wild', 'lastcall', 'finish', 'pyle', 'unlock'];
const SFX_KEY = 'wt_sound';
const SFX_VOLUME = 0.7;
let sfxCtx = null;
let sfxGain = null;
const sfxBuffers = {};

function soundOn() {
    try { return window.localStorage.getItem(SFX_KEY) !== 'off'; } catch (e) { return true; }
}

function initSound() {
    syncSoundToggle();
    if (!document.querySelector('.game-container') || !window.AudioContext && !window.webkitAudioContext) return;
    if (navigator.audioSession) {
        try { navigator.audioSession.type = 'ambient'; } catch (e) { /* older WebKit */ }
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    sfxCtx = new Ctx();
    sfxGain = sfxCtx.createGain();
    sfxGain.gain.value = SFX_VOLUME;
    sfxGain.connect(sfxCtx.destination);
    // A page that opens on a Wyld Pyle (a finished game from history, a
    // shared result) plays its sound as soon as it has loaded, if that's
    // still early in the celebration.
    const onLoad = document.querySelector('.wyld-pyle-celebration') ? 'pyle' : null;
    const loadDeadline = Date.now() + SFX_ON_LOAD_WINDOW_MS;
    const v = document.body.dataset.cacheVersion || '';
    SFX_NAMES.forEach(name => {
        fetch('/static/sfx/' + name + '.mp3?v=' + v)
            .then(r => r.ok ? r.arrayBuffer() : Promise.reject(r.status))
            .then(buf => new Promise((ok, fail) => sfxCtx.decodeAudioData(buf, ok, fail)))
            .then(decoded => {
                sfxBuffers[name] = decoded;
                if (name === onLoad) sfxIfAllowed(name, loadDeadline);
            })
            .catch(() => { /* that one stays silent */ });
    });
    // Unlock on the first touch. The listener stays: iOS can suspend the
    // context again when the app goes to the background.
    const unlock = () => { if (sfxCtx.state !== 'running') sfxCtx.resume().catch(() => {}); };
    ['pointerdown', 'touchend', 'keydown'].forEach(ev => document.addEventListener(ev, unlock, { capture: true, passive: true }));
}

// sfxIfAllowed plays a sound nobody tapped for, on page load. The
// native apps allow that; a browser may keep the page silent until it's
// tapped, and then the sound is skipped rather than played late.
const SFX_ON_LOAD_WINDOW_MS = 1500;

function sfxIfAllowed(name, deadline) {
    if (!sfxCtx || !soundOn()) return;
    const play = () => { if (sfxCtx.state === 'running' && Date.now() < deadline) sfx(name); };
    if (sfxCtx.state === 'running') play();
    else sfxCtx.resume().then(play).catch(() => {});
}

function sfx(name) {
    if (!sfxCtx || !soundOn() || !sfxBuffers[name]) return;
    if (sfxCtx.state !== 'running') sfxCtx.resume().catch(() => {});
    const src = sfxCtx.createBufferSource();
    src.buffer = sfxBuffers[name];
    src.connect(sfxGain);
    src.start();
}

function toggleSound() {
    const on = !soundOn();
    try { window.localStorage.setItem(SFX_KEY, on ? 'on' : 'off'); } catch (e) { /* storage blocked */ }
    syncSoundToggle();
    syncAdMute();
    if (on) sfx('tap');
    track('sound_toggle', { on: on });
}

function syncSoundToggle() {
    const btn = document.getElementById('sound-toggle');
    if (!btn) return;
    const on = soundOn();
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.title = on ? 'Sound on' : 'Sound off';
    btn.querySelector('use').setAttribute('href', on ? '#i-sound' : '#i-sound-off');
}

// Muting the game mutes AdMob's video ads too. Told to the SDK on init and
// on each toggle; it applies to the next ad shown.
function syncAdMute() {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.AdMob) return;
    Capacitor.Plugins.AdMob.setApplicationMuted({ muted: !soundOn() }).catch(() => {});
}

// Words and the end of a game are decided by the server, so they're heard
// by comparing the board before and after each swap: a row that just
// turned valid or invalid, a score summary that just appeared, or a Wyld
// Pyle overlay that just arrived (some endings bring one without a score
// summary: the tutorial, and a multiplayer board finished before the
// others).
let sfxBefore = null;

function sfxBoardState() {
    const grid = document.getElementById('word-grid');
    const rows = cls => new Set(Array.from(document.querySelectorAll('#word-grid .word-row.' + cls)).map(r => r.dataset.wordIndex));
    return {
        game: grid ? grid.dataset.gameId : null,
        valid: rows('word-valid'),
        invalid: rows('word-invalid'),
        scored: !!document.querySelector('.score-summary[data-own]'),
        pyles: Array.from(document.querySelectorAll('.wyld-pyle-celebration')),
    };
}

function sfxAfterSwap() {
    const before = sfxBefore;
    sfxBefore = null;
    if (!before) return;
    const after = sfxBoardState();
    if (after.pyles.some(p => !before.pyles.includes(p))) {
        sfx('pyle');
        return;
    }
    if (!before.scored && after.scored) {
        sfx('finish');
        return;
    }
    if (!after.game || after.game !== before.game) return;
    const fresh = (now, then) => Array.from(now).some(i => !then.has(i));
    if (fresh(after.valid, before.valid)) sfx('word');
    else if (fresh(after.invalid, before.invalid)) sfx('invalid');
}

function initCapacitor() {
    if (!isCapacitor()) return;

    // The server sets this class for native requests so the footer and
    // safe-area padding are right on first paint; keep this for any page
    // that reaches the app without the WyldTyleApp user agent.
    document.body.classList.add('capacitor-app');

    // Universal Links: when a wyldtyle.com link opens the app (cold start
    // or while running), go to that page instead of staying wherever the
    // webview happened to be.
    initAppLinks();

    // Android back button
    if (Capacitor.Plugins.App) {
        Capacitor.Plugins.App.addListener('backButton', ev => {
            if (ev.canGoBack) {
                window.history.back();
            } else {
                Capacitor.Plugins.App.exitApp();
            }
        });
        // Back from the background: catch up at once. The page's own
        // visibilitychange isn't reliable in Android's WebView.
        Capacitor.Plugins.App.addListener('resume', () => {
            refreshLiveViews();
            setTimeout(healWebViewFrame, 300);
        });
    }

    // A webview left short by the Keyboard plugin stays short across page
    // loads, so check on every load and after every keyboard dismissal.
    healWebViewFrame();
    window.addEventListener('keyboardDidHide', () => setTimeout(healWebViewFrame, 100));

    // The app version at the foot of the menu, for support questions.
    showAppVersion();

    // Push notification registration
    initPushNotifications();

    // AdMob initialization (interstitials only for now)
    initAdMob();

    // Google / Apple sign-in through the platform's own sheet
    initNativeSignIn();
}

// --- Native Google / Apple sign-in ---
//
// Google refuses sign-in inside a WebView, so the apps use the platform
// sign-in sheet (@capgo/capacitor-social-login) and post its ID token to
// /auth/{provider}/native. The server renders the buttons hidden; they are
// shown only when the plugin is present, so older app builds never see them.
// See docs/native-sign-in.md.

const GOOGLE_WEB_CLIENT_ID = '533082851396-c25eg84uvpv719actv26t352gu2spuct.apps.googleusercontent.com';
const GOOGLE_IOS_CLIENT_ID = '533082851396-6ik91pgua52pt9p67jsq2llmsclqnjoe.apps.googleusercontent.com';

function showAppVersion() {
    const el = document.querySelector('[data-app-version]');
    const App = Capacitor.Plugins && Capacitor.Plugins.App;
    if (!el || !App || !App.getInfo) return;
    App.getInfo().then(info => {
        el.textContent = 'WyldTyle ' + info.version;
        el.hidden = false;
    }).catch(() => {});
}

async function initNativeSignIn() {
    const SocialLogin = Capacitor.Plugins && Capacitor.Plugins.SocialLogin;
    const buttons = document.querySelectorAll('[data-native-signin]');
    if (!SocialLogin || !buttons.length) return;
    const isAndroid = Capacitor.getPlatform() === 'android';

    try {
        // Apple has no native SDK on Android; its button uses the web flow
        // instead, so only initialize the plugin's Apple provider on iOS.
        // useProperTokenExchange only makes it hand back Apple's authorization
        // code (no network call); the server trades that for the refresh token
        // it revokes on account deletion.
        await SocialLogin.initialize(isAndroid
            ? { google: { webClientId: GOOGLE_WEB_CLIENT_ID } }
            : { google: { webClientId: GOOGLE_WEB_CLIENT_ID, iOSClientId: GOOGLE_IOS_CLIENT_ID }, apple: { useProperTokenExchange: true } });
    } catch (e) {
        console.warn('SocialLogin initialize failed', e);
        return;
    }

    for (const btn of buttons) {
        const provider = btn.dataset.nativeSignin;
        if (provider === 'apple' && isAndroid) {
            // capacitor.config allows appleid.apple.com, so this stays in the WebView.
            btn.addEventListener('click', () => { location.href = '/auth/apple/start'; });
        } else {
            btn.addEventListener('click', () => nativeSignIn(SocialLogin, provider, btn));
        }
        btn.hidden = false;
    }
    document.querySelectorAll('[data-native-signin-group]').forEach(el => { el.hidden = false; });
    // Sign-up's password form: with the buttons showing it becomes the
    // fold-away alternative, as on the web (sign-up.html).
    document.querySelectorAll('details.auth-more[data-native]').forEach(d => {
        d.classList.add('native-ready');
        if (d.dataset.native === 'collapse') d.open = false;
    });
}

async function nativeSignIn(SocialLogin, provider, btn) {
    btn.disabled = true;
    try {
        const nonceRes = await fetch('/auth/native/nonce', { credentials: 'same-origin' });
        if (!nonceRes.ok) throw new Error('Sign-in is unavailable right now. Please try again.');
        const { nonce } = await nonceRes.json();

        // No email or name: only the account ID is kept. forcePrompt makes iOS
        // Google run a fresh sign-in (carrying this nonce) instead of reusing
        // the cached one. Android's plugin rejects custom scopes, so it gets
        // none and its defaults are ignored server-side.
        const options = { nonce };
        if (provider === 'apple') {
            options.scopes = [];
        } else if (Capacitor.getPlatform() === 'ios') {
            options.scopes = [];
            options.forcePrompt = true;
        }
        const res = await SocialLogin.login({ provider, options });
        const result = res.result || {};
        const body = { id_token: result.idToken };
        if (result.authorizationCode) body.authorization_code = result.authorizationCode;

        const r = await fetch(`/auth/${provider}/native`, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const out = await r.json().catch(() => ({}));
        if (r.ok && out.next) {
            location.href = out.next;
            return;
        }
        throw new Error(out.error || 'Sign-in failed. Please try again.');
    } catch (e) {
        if (e && e.code === 'USER_CANCELLED') return; // closed the sheet
        showNativeSignInError(btn, (e && e.message) || 'Sign-in failed. Please try again.');
    } finally {
        btn.disabled = false;
    }
}

function showNativeSignInError(btn, message) {
    const page = btn.closest('.auth-page');
    if (!page) return;
    let box = page.querySelector('.auth-error');
    if (!box) {
        box = document.createElement('div');
        box.className = 'auth-error';
        page.insertBefore(box, page.querySelector('.signin-providers'));
    }
    box.textContent = message;
}

// One-shot callback to run after the next AdMob interstitial is dismissed,
// in place of the default upgrade-pitch cadence. The guest flow uses this
// to start the game once its pre-game ad closes.
let _afterInterstitialDismiss = null;

function initAdMob() {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.AdMob) return;
    const AdMob = Capacitor.Plugins.AdMob;

    // Request App Tracking Transparency (iOS), then initialize AdMob so it
    // serves ads consistent with the user's choice. requestATT resolves
    // immediately (no prompt) when it's skipped, so AdMob still initializes.
    requestATT(AdMob).finally(() => {
        AdMob.initialize({}).then(syncAdMute).catch(err => {
            console.warn('AdMob init failed', err);
        });
    });

    // Hide the status bar the instant the ad is actually on screen, not
    // just before we call show(). The end-of-game interstitial fires during
    // a large results swap and the pre-show hide didn't reliably stick, so
    // the close button stayed under the status bar. This catches every
    // interstitial at the moment it presents.
    AdMob.addListener('interstitialAdShowed', () => hideStatusBarForAd());

    // After an interstitial closes, run the same upgrade-pitch cadence
    // we use on the web. The web side calls maybeShowUpgradePitch from
    // closeInterstitialAd; on mobile the SDK manages the lifecycle, so
    // we hook the dismiss event instead.
    AdMob.addListener('interstitialAdDismissed', () => {
        // The full-screen interstitial leaves iOS in status-bar overlay
        // mode, so on dismiss the web header slides up under the clock.
        // Re-assert the StatusBar layout to keep the webview inset.
        restoreStatusBar();
        const cb = _afterInterstitialDismiss;
        _afterInterstitialDismiss = null;
        if (cb) cb();
        else maybeShowUpgradePitch();
    });
}

// Shows the iOS App Tracking Transparency prompt once (when the status is
// still 'notDetermined'), then resolves so AdMob can initialize.
//
// Gated to build >= 3: requestTrackingAuthorization() requires
// NSUserTrackingUsageDescription in Info.plist, first added in build 3.
// Calling it on an earlier build (e.g. the live build 2) crashes the app,
// and a native crash isn't catchable from JS — so we check the build number
// first. Resolves immediately (AdMob still initializes) when skipped.
async function requestATT(AdMob) {
    try {
        if (!Capacitor.getPlatform || Capacitor.getPlatform() !== 'ios') return;
        if (!Capacitor.Plugins.App) return;
        const info = await Capacitor.Plugins.App.getInfo();
        if (parseInt(info.build, 10) < 3) return;
        const { status } = await AdMob.trackingAuthorizationStatus();
        if (status === 'notDetermined') {
            await AdMob.requestTrackingAuthorization();
        }
    } catch (e) {
        console.warn('ATT request failed', e);
    }
}

// Re-applies the status-bar config that capacitor.config.json sets at
// launch. A full-screen AdMob interstitial resets iOS to overlay mode on
// dismiss, which lets the header render under the system status bar.
// No-op on web and on Android, where Capacitor's SystemBars plugin keeps
// the insets correct (see MainActivity.java).
function restoreStatusBar() {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.StatusBar) return;
    if (Capacitor.getPlatform && Capacitor.getPlatform() !== 'ios') return;
    const StatusBar = Capacitor.Plugins.StatusBar;
    // Show it again — it's hidden while an interstitial is up (see below).
    StatusBar.show().catch(() => {});
    StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    syncThemeChrome();
}

// Restores a webview the Keyboard plugin left short. In its default
// "native" mode the plugin shrinks the webview's frame by the keyboard's
// height and grows it back only on a matching keyboard-hide, which
// sometimes never arrives; the app then opens with the bottom ~40% of the
// screen black. When no field is focused but the page is shorter than the
// screen, toggling the status-bar overlay makes the StatusBar plugin
// recompute the frame from the window's bounds. No-op off iOS.
function healWebViewFrame() {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.StatusBar) return;
    if (Capacitor.getPlatform && Capacitor.getPlatform() !== 'ios') return;
    const focused = document.activeElement;
    if (focused && focused.matches('input, textarea, select, [contenteditable]')) return;
    const StatusBar = Capacitor.Plugins.StatusBar;
    StatusBar.getInfo().then(info => {
        if (!info.visible || info.overlays) return;
        const landscape = window.innerWidth > window.innerHeight;
        const screenHeight = landscape
            ? Math.min(screen.width, screen.height)
            : Math.max(screen.width, screen.height);
        const expected = screenHeight - (info.height || 0);
        if (window.innerHeight >= expected - 40) return;
        track('webview_frame_healed', { short_by: Math.round(expected - window.innerHeight) });
        StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
        StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    }).catch(() => {});
}

// --- Status bar / theme-color sync ---
//
// The native status bar is painted by the StatusBar plugin, not by page CSS
// (overlaysWebView is false, so it sits above the webview). Left alone it
// keeps the launch color from capacitor.config.json — classic board green —
// which shows as a green strip above a neon or minimal header. Same story on
// the web, where the theme-color meta tag is a fixed green. syncThemeChrome
// reads the active theme's header color out of the page and pushes it to
// both, so the top of the screen always matches the header.

function toHexColor(value) {
    const s = (value || '').trim();
    if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
    if (/^#[0-9a-f]{3}$/i.test(s)) {
        return '#' + s.slice(1).split('').map(c => c + c).join('').toLowerCase();
    }
    const m = s.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (!m) return null;
    return '#' + m.slice(1, 4)
        .map(n => Math.round(parseFloat(n)).toString(16).padStart(2, '0'))
        .join('');
}

// Rec. 601 luma — precise enough to pick light vs dark status-bar text.
function isLightColor(hex) {
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
}

// The header's background (var(--board)), resolved to a hex string for the
// native plugin and the meta tag. Falls back to the custom property itself
// when the header isn't in the DOM.
function headerColor() {
    const header = document.querySelector('header');
    const raw = header
        ? getComputedStyle(header).backgroundColor
        : getComputedStyle(document.body).getPropertyValue('--board');
    return toHexColor(raw);
}

function syncThemeChrome() {
    const color = headerColor();
    if (!color) return;

    // Only the unconditional tag — the "system" theme ships a light/dark
    // pair gated by media attributes, which is already correct per scheme.
    const meta = document.querySelector('meta[name="theme-color"]:not([media])');
    if (meta) meta.setAttribute('content', color);

    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.StatusBar) return;
    const StatusBar = Capacitor.Plugins.StatusBar;
    StatusBar.setBackgroundColor({ color }).catch(() => {});
    // Plugin naming is inverted: 'DARK' means light text (for a dark
    // background), 'LIGHT' means dark text.
    StatusBar.setStyle({ style: isLightColor(color) ? 'LIGHT' : 'DARK' }).catch(() => {});
}
// The profile page's theme picker swaps data-theme live, without a reload.
window.syncThemeChrome = syncThemeChrome;

// On the "system" theme the palette flips with the device setting, with no
// page load to hang the sync off — watch the media query instead.
if (window.matchMedia) {
    const darkMode = window.matchMedia('(prefers-color-scheme: dark)');
    if (darkMode.addEventListener) {
        darkMode.addEventListener('change', () => syncThemeChrome());
    }
}

// Hides the iOS status bar while a full-screen interstitial is shown. The
// AdMob SDK puts its close (X) button in the top-right corner, where the
// system status bar overlaps it and a finger tap can't reliably land — so
// on device the ad looks impossible to close (works with a mouse on the
// simulator, which lands the exact pixel). Hiding the status bar clears
// that region; restoreStatusBar() shows it again on dismiss. No-op off iOS.
function hideStatusBarForAd() {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.StatusBar) return;
    if (Capacitor.getPlatform && Capacitor.getPlatform() !== 'ios') return;
    Capacitor.Plugins.StatusBar.hide().catch(() => {});
}

// --- Web Push (browser / desktop) ---
//
// initWebPush sets up the opt-in button and re-syncs any existing
// subscription with the server so the user's account stays linked even
// if browser cookies got reset since they last subscribed.

function webPushSupported() {
    return 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

async function postSubscription(subscription) {
    const body = new URLSearchParams({
        token: JSON.stringify(subscription),
        platform: 'web',
    });
    await fetch('/push/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
    });
}

// Called from the explicit opt-in button. Browsers block silent
// requestPermission() calls outside a user gesture, so this function
// must run inside an onclick handler.
async function subscribeWebPush() {
    if (!webPushSupported()) {
        alert('Notifications aren’t supported in this browser.');
        return false;
    }
    const vapidKey = document.body.dataset.vapidPublicKey;
    if (!vapidKey) {
        console.warn('VAPID public key not present on page');
        return false;
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
    }
    await postSubscription(sub);
    updateWebPushUI('granted');
    return true;
}
window.subscribeWebPush = subscribeWebPush;

// Toggle a single notification type on/off. Wired to the checkboxes
// in Profile → Settings → Notification preferences.
async function setNotificationPref(checkbox) {
    const type = checkbox.dataset.notificationPref;
    if (!type) return;
    const enabled = checkbox.checked;
    checkbox.disabled = true;
    try {
        const body = new URLSearchParams({ type, enabled: enabled ? 'true' : 'false' });
        const res = await fetch('/profile/notification-prefs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        });
        if (!res.ok) throw new Error(res.statusText);
    } catch (err) {
        console.warn('setNotificationPref failed', err);
        checkbox.checked = !enabled; // revert on failure
    } finally {
        checkbox.disabled = false;
    }
}
window.setNotificationPref = setNotificationPref;

// --- Engagement-timed push nudge (home page banner) ---

function initPushNudge() {
    const banner = document.getElementById('push-nudge-banner');
    if (!banner) return;
    // Hide for contexts where the nudge can't / shouldn't apply.
    if (isCapacitor() || !webPushSupported() || !document.body.dataset.vapidPublicKey) return;
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
    banner.hidden = false;
}

async function acceptPushNudge() {
    const banner = document.getElementById('push-nudge-banner');
    const granted = await subscribeWebPush();
    if (banner) banner.hidden = true;
    // Always mark dismissed — whether they granted or not, we don't
    // want to keep nagging them every visit.
    fetch('/profile/push-nudge-dismiss', { method: 'POST' });
    return granted;
}
window.acceptPushNudge = acceptPushNudge;

function dismissPushNudge() {
    const banner = document.getElementById('push-nudge-banner');
    if (banner) banner.hidden = true;
    fetch('/profile/push-nudge-dismiss', { method: 'POST' });
}
window.dismissPushNudge = dismissPushNudge;

function detectBrowser() {
    const ua = navigator.userAgent;
    if (/Edg\//.test(ua)) return 'edge';
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'chrome';
    if (/Firefox\//.test(ua)) return 'firefox';
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'safari';
    return 'other';
}

function unblockInstructions() {
    switch (detectBrowser()) {
        case 'chrome':
            return 'In Chrome: click the lock icon at the left end of the address bar → Notifications → Allow, then reload.';
        case 'edge':
            return 'In Edge: click the lock icon at the left end of the address bar → Permissions for this site → Notifications → Allow, then reload.';
        case 'firefox':
            return 'In Firefox: click the lock icon → Connection secure → More information → Permissions → uncheck "Block" next to Send Notifications, then reload.';
        case 'safari':
            return 'In Safari: Safari menu → Settings → Websites → Notifications → find wyldtyle.com and set it to Allow, then reload.';
        default:
            return 'Find this site in your browser\'s notification settings and switch it to Allow, then reload.';
    }
}

function updateWebPushUI(state) {
    const btn = document.getElementById('web-push-enable');
    const status = document.getElementById('web-push-status');
    if (!btn) return;
    if (state === 'granted') {
        btn.style.display = 'none';
        if (status) {
            status.textContent = 'Notifications enabled in this browser.';
            status.hidden = false;
        }
    } else if (state === 'denied') {
        btn.style.display = 'none';
        if (status) {
            status.textContent = 'Notifications are blocked. ' + unblockInstructions();
            status.hidden = false;
        }
    } else {
        btn.style.display = '';
        if (status) status.hidden = true;
    }
}

async function initWebPush() {
    const section = document.getElementById('web-push-section');
    // Hide the opt-in section for contexts that can't use web push
    // (Capacitor native, browsers without push, signed-out users, or
    // when the server hasn't configured VAPID).
    if (isCapacitor() || !webPushSupported() || !document.body.dataset.vapidPublicKey) {
        if (section) section.hidden = true;
        return;
    }
    if (!document.querySelector('.nav-avatar')) return; // signed in only

    // Reflect current permission state in the UI.
    updateWebPushUI(Notification.permission);

    // If the user already granted previously, refresh the subscription
    // server-side so a new device/browser stays linked even if their
    // session cookie rotated.
    if (Notification.permission === 'granted') {
        try {
            const reg = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) await postSubscription(sub);
        } catch (err) {
            console.warn('web push refresh failed', err);
        }
    }
}

function initPushNotifications() {
    const PushNotifications = Capacitor.Plugins.PushNotifications;
    if (!PushNotifications) return;

    // Only register if user is signed in
    if (!document.querySelector('.nav-avatar')) return;

    PushNotifications.requestPermissions().then(result => {
        if (result.receive === 'granted') {
            PushNotifications.register();
        }
    });

    PushNotifications.addListener('registration', token => {
        const platform = Capacitor.getPlatform();
        fetch('/push/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: 'token=' + encodeURIComponent(token.value) + '&platform=' + encodeURIComponent(platform)
        });
    });

    // A push while the app is open (a friend moved, a new invite): the
    // match board or home list on screen updates at once instead of at
    // its next poll.
    PushNotifications.addListener('pushNotificationReceived', () => refreshLiveViews());

    PushNotifications.addListener('pushNotificationActionPerformed', notification => {
        const data = notification.notification.data;
        if (data && data.url) {
            window.location.href = data.url;
        }
    });
}

// Read action base and board target from the word grid data attributes
function getActionBase() {
    const grid = document.getElementById('word-grid');
    return grid ? grid.dataset.actionBase : '';
}

function getBoardTarget() {
    const grid = document.getElementById('word-grid');
    return grid ? grid.dataset.boardTarget : '#game-board';
}

function getGameID() {
    const grid = document.getElementById('word-grid');
    return grid ? grid.dataset.gameId : '';
}

// --- Dice Picker ---

let dicePicks = [];

function pickDie(el, type) {
    if (dicePicks.length >= 2) return;
    if (!tutorialAllows('die', type)) return;

    haptic('light');
    sfx('tap');

    // Add bounce animation to the clicked die
    el.classList.add('die-picked');

    dicePicks.push(type);
    tutorialSync();

    // After the first pick, say what the second tap can be. The rule that
    // trips people up is that the same die can be tapped again.
    if (dicePicks.length === 1) {
        const prompt = document.querySelector('#dice-picker .dice-prompt');
        const hint = document.querySelector('#dice-picker .dice-hint');
        if (prompt) prompt.textContent = type === 'c' ? 'Tap it again, or add a vowel' : 'Tap it again, or add a consonant';
        if (hint) hint.style.display = 'none';
    }

    if (dicePicks.length === 2) {
        // Determine roll type
        const sorted = dicePicks.slice().sort().join('');
        let rollType = 'cv';
        if (sorted === 'cc') rollType = 'cc';
        else if (sorted === 'vv') rollType = 'vv';

        sfx('roll');
        // Animate only the dice that were picked
        document.querySelectorAll('.die').forEach(d => {
            const dieType = d.dataset.type;
            if (dicePicks.indexOf(dieType) >= 0) {
                d.classList.add('die-rolling');
            }
        });

        // After animation, send the roll
        setTimeout(() => {
            const picker = document.getElementById('dice-picker');
            const actionBase = picker ? picker.dataset.actionBase : '';
            const boardTarget = picker ? picker.dataset.boardTarget : '#game-board';
            const endpoint = picker && picker.dataset.rollEndpoint ? picker.dataset.rollEndpoint : '/roll-type';

            htmx.ajax('POST', actionBase + endpoint, {
                target: boardTarget,
                swap: 'outerHTML',
                values: { roll_type: rollType }
            });

            dicePicks = [];
        }, 800);
    }
}

// --- Letter Placement ---

function selectBankLetter(index, letter) {
    if (!tutorialAllows('bank', index)) return;
    selectedBankIndex = index;
    selectedBankLetter = letter;
    haptic('light');
    sfx('select');

    // Highlight selected bank tile
    document.querySelectorAll('.bank-tile').forEach(el => el.classList.remove('selected'));
    const tile = document.querySelector(`.bank-tile[data-bank-index="${index}"]`);
    if (tile) tile.classList.add('selected');

    // Highlight slots as targets. In trade-placing every placeable slot
    // (filled or empty) is a valid swap target; in normal placing only
    // empty slots accept a letter.
    const grid = document.getElementById('word-grid');
    const phase = grid ? grid.dataset.phase : '';
    const selector = phase === 'trade_placing' ? '.slot.placeable' : '.slot.empty.placeable';
    document.querySelectorAll(selector).forEach(el => el.classList.add('target'));
    tutorialSync();
}

// Letter values, mirrored from the engine, for the instant tile below.
// A wild scores WILD_POINTS whatever letter it plays as (game.WildPoints).
const WILD_POINTS = 1;
const LETTER_POINTS = {A:2,B:4,C:3,D:2,E:1,F:3,G:3,H:2,I:2,K:5,L:2,M:3,N:1,O:2,P:4,R:1,S:1,T:1,U:3,V:5,W:3,Y:4};

// Draw the tile in its slot the moment it's tapped, before the server
// answers. The server's board replaces the whole grid a moment later; on
// a cold Lambda that can be over a second, and without this the tap
// looks ignored. If the request fails, the page reloads to the truth.
function showPlacedTile(wordIndex, slotIndex, letter, isWild, bankIndex) {
    const slot = document.querySelector(`.slot[data-word-index="${wordIndex}"][data-slot-index="${slotIndex}"]`);
    if (slot) {
        slot.classList.remove('empty', 'target', 'placeable');
        slot.classList.add('pending-place');
        if (isWild) slot.classList.add('wild');
        const pts = isWild ? WILD_POINTS : (LETTER_POINTS[letter] || 0);
        slot.innerHTML = `<span class="tile-letter">${letter}</span><span class="tile-points">${pts}</span>`;
        slot.removeAttribute('onclick');
    }
    const bank = document.querySelector(`.bank-tile[data-bank-index="${bankIndex}"]`);
    if (bank) bank.classList.add('bank-used');
    document.querySelectorAll('.slot.target').forEach(el => el.classList.remove('target'));
}

document.addEventListener('htmx:responseError', ev => {
    if (ev.detail && ev.detail.pathInfo && /\/place$/.test(ev.detail.pathInfo.requestPath || '')) {
        window.location.reload();
    }
});

function placeLetterInSlot(wordIndex, slotIndex) {
    if (selectedBankIndex < 0) return;
    if (!tutorialAllows('slot', wordIndex + ':' + slotIndex)) return;

    // If placing a wild letter, show the letter picker
    if (selectedBankLetter === '*') {
        pendingWildPlacement = { wordIndex, slotIndex };
        showWildPicker();
        return;
    }

    haptic('medium');
    sfx('place');
    showPlacedTile(wordIndex, slotIndex, selectedBankLetter, false, selectedBankIndex);
    htmx.ajax('POST', `${getActionBase()}/place`, {
        target: getBoardTarget(),
        swap: 'outerHTML',
        values: {
            bank_index: selectedBankIndex,
            word_index: wordIndex,
            slot_index: slotIndex
        }
    });

    selectedBankIndex = -1;
    selectedBankLetter = '';
}

function confirmWildLetter(letter) {
    if (!pendingWildPlacement || selectedBankIndex < 0) return;
    if (!tutorialAllows('wild', letter)) return;

    haptic('medium');
    sfx('place');
    showPlacedTile(pendingWildPlacement.wordIndex, pendingWildPlacement.slotIndex, letter, true, selectedBankIndex);
    htmx.ajax('POST', `${getActionBase()}/place`, {
        target: getBoardTarget(),
        swap: 'outerHTML',
        values: {
            bank_index: selectedBankIndex,
            word_index: pendingWildPlacement.wordIndex,
            slot_index: pendingWildPlacement.slotIndex,
            letter: letter
        }
    });

    closeWildPicker();
    selectedBankIndex = -1;
    selectedBankLetter = '';
    pendingWildPlacement = null;
}

let wildPickerReady = false;

function showWildPicker() {
    const overlay = document.getElementById('wild-picker-overlay');
    if (!overlay) return;
    wildPickerReady = false;
    // In the tutorial only the scripted letter can be chosen.
    const coach = document.getElementById('tutorial-coach');
    const wild = coach ? coach.dataset.wild : '';
    overlay.querySelectorAll('.wild-pick-btn').forEach(btn => {
        const off = !!wild && btn.textContent.trim() !== wild;
        btn.classList.toggle('tutorial-off', off);
        btn.classList.toggle('tutorial-target', !!wild && !off);
    });
    renderWildPreview('');
    overlay.style.display = '';
    // Prevent touch-through: delay before backdrop tap can dismiss
    requestAnimationFrame(() => { wildPickerReady = true; });
}

// The picker shows the word the wild is going into, its slot marked,
// so the choice is made against the letters already there. Pointing
// at a letter (mouse or keyboard focus) tries it in the slot.
function renderWildPreview(letter) {
    const preview = document.getElementById('wild-picker-preview');
    if (!preview || !pendingWildPlacement) return;
    const { wordIndex, slotIndex } = pendingWildPlacement;
    const slots = document.querySelectorAll(`#word-grid .slot[data-word-index="${wordIndex}"]`);
    preview.replaceChildren(...Array.from(slots, (slot, i) => {
        const tile = document.createElement('span');
        if (i === slotIndex) {
            tile.className = 'pattern-tile target';
            tile.textContent = letter;
        } else {
            const ch = slot.querySelector('.tile-letter');
            tile.className = 'pattern-tile' + (ch ? '' : ' open');
            tile.textContent = ch ? ch.textContent.trim() : '';
        }
        return tile;
    }));
}

document.addEventListener('DOMContentLoaded', () => {
    const grid = document.querySelector('.wild-picker-grid');
    if (!grid) return;
    const tryLetter = ev => {
        const btn = ev.target.closest('.wild-pick-btn');
        if (btn) renderWildPreview(btn.textContent.trim());
    };
    grid.addEventListener('pointerover', ev => { if (ev.pointerType === 'mouse') tryLetter(ev); });
    grid.addEventListener('focusin', tryLetter);
    grid.addEventListener('pointerleave', () => renderWildPreview(''));
});

function closeWildPicker() {
    if (!wildPickerReady) return;
    const overlay = document.getElementById('wild-picker-overlay');
    if (overlay) overlay.style.display = 'none';
    pendingWildPlacement = null;
}

// --- Tutorial ---
//
// /tutorial is a scripted practice board (internal/server/tutorial.go).
// Its coach (#tutorial-coach) names the one tap each step allows; the
// tap handlers above ask tutorialAllows first, and tutorialSync lights
// the next target. Off the tutorial there's no coach and both are no-ops.

const TUTORIAL_KEY = 'wt_tutorial';

function tutorialSeen() {
    try { return !!window.localStorage.getItem(TUTORIAL_KEY); } catch (e) { return false; }
}

function markTutorialSeen() {
    try { window.localStorage.setItem(TUTORIAL_KEY, '1'); } catch (e) { /* storage blocked */ }
}

// The dice still to tap this step: the step's pair less what's been picked.
function tutorialDiceLeft(dice) {
    const left = (dice || '').split('');
    dicePicks.forEach(p => {
        const i = left.indexOf(p);
        if (i >= 0) left.splice(i, 1);
    });
    return left;
}

function tutorialAllows(kind, value) {
    const coach = document.getElementById('tutorial-coach');
    if (!coach) return true;
    const d = coach.dataset;
    switch (kind) {
    case 'die': return d.move === 'roll' && tutorialDiceLeft(d.dice).indexOf(value) >= 0;
    case 'bank': return d.move === 'place' && String(value) === d.bank;
    case 'slot': return d.move === 'place' && value === d.word + ':' + d.slot;
    case 'wild': return !d.wild || value === d.wild;
    }
    return false;
}

function tutorialSync() {
    const coach = document.getElementById('tutorial-coach');
    if (!coach) return;
    const d = coach.dataset;
    document.querySelectorAll('.game-board .tutorial-target').forEach(el => el.classList.remove('tutorial-target'));
    const light = el => { if (el) el.classList.add('tutorial-target'); };
    if (d.move === 'roll') {
        const left = tutorialDiceLeft(d.dice);
        document.querySelectorAll('#dice-picker .die').forEach(die => {
            if (left.indexOf(die.dataset.type) >= 0) light(die);
        });
    } else if (d.move === 'place') {
        const slot = document.querySelector(`.slot[data-word-index="${d.word}"][data-slot-index="${d.slot}"]`);
        if (selectedBankIndex < 0) {
            light(document.querySelector(`.bank-tile[data-bank-index="${d.bank}"]`));
        } else {
            document.querySelectorAll('.slot.target').forEach(el => { if (el !== slot) el.classList.remove('target'); });
            light(slot);
        }
    } else if (d.move === 'finish') {
        light(document.querySelector('.end-turn-btn'));
    }
}

function initTutorial() {
    const coach = document.getElementById('tutorial-coach');
    if (coach && coach.dataset.step === '1') {
        markTutorialSeen();
        track('tutorial_start');
    }
    tutorialSync();
    initTutorialOffer();
}

function skipTutorial(ev) {
    const coach = document.getElementById('tutorial-coach');
    track('tutorial_skip', { step: coach ? parseInt(coach.dataset.step, 10) : 0 });
    markTutorialSeen();
}

// The offer: a new player's first tap on a Play button marked
// data-offer-tutorial opens the "New to WyldTyle?" sheet instead. Skipping
// carries on with the tap that was held back. Asked once per device.
let tutorialOfferFrom = null;

function initTutorialOffer() {
    const nudge = document.getElementById('tutorial-nudge');
    if (nudge && tutorialSeen()) nudge.remove();
    if (!document.getElementById('tutorial-offer-modal') || tutorialSeen()) return;
    document.addEventListener('click', ev => {
        const el = ev.target.closest && ev.target.closest('[data-offer-tutorial]');
        if (!el || tutorialSeen()) return;
        ev.preventDefault();
        ev.stopPropagation();
        tutorialOfferFrom = el;
        document.getElementById('tutorial-offer-modal').style.display = '';
        track('tutorial_offer');
    }, true);
}

// The home card's ×: not interested, so the Play buttons won't ask either.
function dismissTutorialNudge() {
    const nudge = document.getElementById('tutorial-nudge');
    if (nudge) nudge.remove();
    markTutorialSeen();
    track('tutorial_nudge_dismiss');
}

function acceptTutorial() {
    track('tutorial_accept');
}

// Skip (proceed) carries on to the game they tapped; the close button or
// the backdrop just closes the sheet.
function declineTutorial(proceed) {
    const modal = document.getElementById('tutorial-offer-modal');
    if (modal) modal.style.display = 'none';
    if (!proceed) return;
    markTutorialSeen();
    track('tutorial_decline');
    const el = tutorialOfferFrom;
    tutorialOfferFrom = null;
    if (el) el.click();
}

// --- Analytics (GA4) ---
//
// gtag is configured in base.html when a measurement ID is set, on the
// web only; every gtag call here is a no-op without it. Browser-side moments call track()
// directly. Server-decided moments (game created, game just finished,
// signed in) arrive as hidden [data-ga-event] markers in the page or in
// an HTMX fragment, and flushGAEvents() sends each once. In the app
// events go to Firebase Analytics instead, which is also what Google Ads
// App campaigns count conversions from (builds before the plugin send
// nothing).
function track(name, params) {
    const firebase = isCapacitor() && Capacitor.Plugins && Capacitor.Plugins.FirebaseAnalytics;
    if (firebase) {
        firebase.logEvent({ name: name, params: params || {} }).catch(() => { /* never break the page */ });
    }
    if (typeof gtag !== 'function') return;
    try { gtag('event', name, params || {}); } catch (e) { /* never break the page */ }
}
window.track = track;

function flushGAEvents() {
    document.querySelectorAll('[data-ga-event]:not([data-ga-sent])').forEach(el => {
        el.setAttribute('data-ga-sent', '1');
        let params = {};
        try { params = JSON.parse(el.dataset.gaParams || '{}'); } catch (e) { /* ignore */ }
        track(el.dataset.gaEvent, params);
    });
}

// The app is one native screen around a webview, so Firebase never sees
// page changes on its own; each page load (and each HTMX navigation that
// changes the URL) is reported as a screen. IDs and dates are folded so
// /game/abc123 and /game/def456 are one screen, /game/:id.
function trackScreen() {
    const firebase = isCapacitor() && Capacitor.Plugins && Capacitor.Plugins.FirebaseAnalytics;
    if (!firebase) return;
    const name = location.pathname
        .split('/')
        .map(seg => /\d/.test(seg) && seg.length >= 6 || /^\d+$/.test(seg) ? ':id' : seg)
        .join('/') || '/';
    firebase.setCurrentScreen({ screenName: name }).catch(() => { /* never break the page */ });
}

function initAnalytics() {
    trackScreen();
    document.addEventListener('htmx:pushedIntoHistory', trackScreen);
    flushGAEvents();
    // Clicks worth counting, wherever the element is rendered.
    document.addEventListener('click', evt => {
        const a = evt.target.closest ? evt.target.closest('a, button') : null;
        if (!a) return;
        if (a.classList.contains('install-banner-get')) track('install_banner_click');
        else if (a.classList.contains('install-banner-close')) track('install_banner_dismiss');
    });
    // Trade decisions go through HTMX.
    document.addEventListener('htmx:afterRequest', evt => {
        const path = evt.detail && evt.detail.pathInfo ? evt.detail.pathInfo.requestPath : '';
        if (!path || !evt.detail.successful) return;
        if (path.endsWith('/trade/roll')) track('trade_used');
        else if (path.endsWith('/trade/skip')) track('trade_skipped');
    });
}

// --- Hints ---

function showHints(wordIndex) {
    const gameID = getGameID();
    fetch(`/game/${gameID}/hints/${wordIndex}`)
        .then(res => {
            if (!res.ok) throw new Error(res.statusText);
            return res.text();
        })
        .then(html => {
            const overlay = document.getElementById('hints-overlay');
            const popup = overlay ? overlay.querySelector('.hints-popup') : null;
            if (popup) {
                popup.innerHTML = html;
                fadeHintsOverflow(popup.querySelector('.hints-list'));
            }
            track('hint_used', { word_index: wordIndex });
            if (overlay) overlay.style.display = '';
        })
        .catch(() => {});
}

// Fade the list's bottom edge while words sit below it, until the
// player scrolls to the end.
function fadeHintsOverflow(list) {
    if (!list) return;
    const update = () => list.classList.toggle('more-below',
        list.scrollTop + list.clientHeight < list.scrollHeight - 4);
    list.addEventListener('scroll', update, { passive: true });
    requestAnimationFrame(update);
}

function closeHints() {
    const overlay = document.getElementById('hints-overlay');
    if (overlay) overlay.style.display = 'none';
}

// --- Result Tabs (finished game) ---

function showResultTab(tab, btn) {
    const board = document.getElementById('result-board');
    const scores = document.getElementById('result-scores');
    if (!board || !scores) return;

    if (tab === 'board') {
        board.style.display = '';
        scores.style.display = 'none';
    } else {
        board.style.display = 'none';
        scores.style.display = '';
    }

    document.querySelectorAll('.result-tab').forEach(t => t.removeAttribute('data-active'));
    btn.setAttribute('data-active', 'true');
}

// --- Custom Select (Security Question) ---

function toggleCustomSelect() {
    const options = document.getElementById('sq-options');
    const select = document.getElementById('sq-select');
    if (!options || !select) return;
    select.classList.toggle('open');
}

function selectQuestion(btn) {
    const value = btn.getAttribute('data-value');
    document.getElementById('security_question').value = value;
    document.getElementById('sq-label').textContent = value;
    document.getElementById('sq-trigger').classList.remove('is-placeholder');
    document.getElementById('sq-select').classList.remove('open');
}

document.addEventListener('click', e => {
    const select = document.getElementById('sq-select');
    if (select && !select.contains(e.target)) {
        select.classList.remove('open');
    }
});

// --- Password Match Feedback (sign-up + password reset) ---

(function initPasswordMatch() {
    const pw = document.getElementById('password');
    const confirm = document.getElementById('confirm_password');
    const error = document.getElementById('confirm-password-error');
    if (!pw || !confirm || !error) return;

    function check() {
        const mismatched = confirm.value && pw.value !== confirm.value;
        error.hidden = !mismatched;
        // Also a validity error, so sign-up's Next and the browser's own
        // submit check stop on it.
        confirm.setCustomValidity(mismatched ? "Passwords don't match" : '');
        if (mismatched) {
            confirm.setAttribute('aria-invalid', 'true');
        } else {
            confirm.removeAttribute('aria-invalid');
        }
    }

    pw.addEventListener('input', check);
    confirm.addEventListener('input', check);
})();

// --- Sign-up steps (sign-up.html) ---
//
// One form shown as three steps: names, password, recovery. Each Next
// checks its step first (step 1 also asks the server whether the names
// are free), Enter moves forward rather than submitting early, and the
// last step submits everything. Without this script all steps show.

(function initSignupSteps() {
    const form = document.querySelector('form.signup-steps');
    if (!form) return;
    const steps = [...form.querySelectorAll('.signup-step')];
    const indicator = form.querySelector('.step-indicator');
    let current = 0;

    form.classList.add('js-steps');
    indicator.hidden = false;
    form.querySelectorAll('.step-nav, [data-step-back]').forEach(el => { el.hidden = false; });

    function show(i, focus) {
        current = i;
        steps.forEach((step, n) => step.classList.toggle('is-current', n === i));
        indicator.querySelector('[data-step-num]').textContent = i + 1;
        if (focus) {
            const first = steps[i].querySelector('input:not([type=hidden]), button.custom-select-trigger');
            if (first) first.focus();
        }
    }

    function fieldError(input, errorEl, msg) {
        errorEl.textContent = msg;
        errorEl.hidden = !msg;
        if (msg) {
            input.setAttribute('aria-invalid', 'true');
        } else {
            input.removeAttribute('aria-invalid');
        }
    }

    // The browser's own checks (required, lengths, passwords match), for
    // this step's fields only.
    function stepValid(i) {
        for (const input of steps[i].querySelectorAll('input:not([type=hidden])')) {
            if (!input.checkValidity()) {
                input.reportValidity();
                return false;
            }
        }
        return true;
    }

    const username = form.querySelector('#username');
    const displayName = form.querySelector('#display_name');
    const usernameError = form.querySelector('#username-error');
    const displayNameError = form.querySelector('#display-name-error');
    username.addEventListener('input', () => fieldError(username, usernameError, ''));
    displayName.addEventListener('input', () => fieldError(displayName, displayNameError, ''));

    // Step 1: are the names free (and allowed)? A failed request lets the
    // player on; the final submit checks again.
    async function namesFree() {
        try {
            const res = await fetch('/sign-up/check', {
                method: 'POST',
                body: new URLSearchParams({ username: username.value.trim(), display_name: displayName.value.trim() }),
                credentials: 'same-origin',
            });
            if (!res.ok) return true;
            const out = await res.json();
            fieldError(username, usernameError, out.username || '');
            fieldError(displayName, displayNameError, out.display_name || '');
            if (out.username) { username.focus(); return false; }
            if (out.display_name) { displayName.focus(); return false; }
        } catch (e) {
            // Offline or similar: let the submit decide.
        }
        return true;
    }

    let busy = false;
    async function next() {
        if (busy || !stepValid(current)) return;
        if (current === 0) {
            busy = true;
            const nextBtn = steps[0].querySelector('[data-step-next]');
            nextBtn.disabled = true;
            const ok = await namesFree();
            nextBtn.disabled = false;
            busy = false;
            if (!ok) return;
        }
        show(current + 1, true);
    }

    form.querySelectorAll('[data-step-next]').forEach(btn => btn.addEventListener('click', next));
    form.querySelectorAll('[data-step-back]').forEach(btn => btn.addEventListener('click', () => show(current - 1, true)));

    const sqInput = form.querySelector('#security_question');
    const sqError = form.querySelector('#sq-error');
    form.querySelectorAll('.custom-select-option').forEach(opt => opt.addEventListener('click', () => { sqError.hidden = true; }));

    // Enter in an earlier step moves on. It has to be caught here: the
    // browser vets the whole form before a submit event, and the later
    // steps' empty fields make it give up silently.
    form.addEventListener('keydown', e => {
        if (e.key === 'Enter' && e.target.tagName === 'INPUT' && current < steps.length - 1) {
            e.preventDefault();
            next();
        }
    });

    form.addEventListener('submit', e => {
        if (current < steps.length - 1) {
            e.preventDefault();
            next();
            return;
        }
        if (!sqInput.value) {
            e.preventDefault();
            sqError.hidden = false;
            form.querySelector('#sq-trigger').focus();
        }
    });

    // After a server error the page comes back on the step at fault.
    const start = Math.min(Math.max(parseInt(form.dataset.startStep, 10) || 1, 1), steps.length) - 1;
    show(start, start > 0);
})();

// --- Other Player Score/Board Tabs ---

const _activeOtherTabs = {};

function showOtherPlayerTab(index, tab, btn) {
    _activeOtherTabs[index] = tab;
    const scores = document.getElementById('mp-other-scores-' + index);
    const board = document.getElementById('mp-other-board-' + index);
    if (!scores || !board) return;
    scores.style.display = tab === 'scores' ? '' : 'none';
    board.style.display = tab === 'board' ? '' : 'none';
    const container = btn.closest('.game-board');
    container.querySelectorAll('.result-tab').forEach(t => t.removeAttribute('data-active'));
    btn.setAttribute('data-active', 'true');
}

// --- Multiplayer Tabs ---

let _activeMPTab = 'mine';

function showMPTab(tab, btn) {
    _activeMPTab = tab;
    const contents = document.querySelectorAll('#mp-game-board .mp-tab-content');
    contents.forEach(el => {
        el.style.display = el.id === 'mp-tab-' + tab ? '' : 'none';
    });
    const tabs = document.querySelectorAll('.mp-tab');
    tabs.forEach(t => t.removeAttribute('data-active'));
    if (btn) btn.setAttribute('data-active', 'true');
}

// Swipe left/right inside #mp-game-board to switch between player tabs.
// Document-level listeners so they survive HTMX swaps of the board element.
function initMPSwipe() {
    let startX = 0, startY = 0, tracking = false;

    document.addEventListener('touchstart', (e) => {
        const board = document.getElementById('mp-game-board');
        if (!board || !board.contains(e.target)) {
            tracking = false;
            return;
        }
        // Skip touches that start on interactive controls so taps and
        // tile/die placements behave normally.
        if (e.target.closest('button, input, textarea, select, a, .tile, .die')) {
            tracking = false;
            return;
        }
        const t = e.changedTouches[0];
        startX = t.clientX;
        startY = t.clientY;
        tracking = true;
    }, { passive: true });

    document.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        // Require a horizontal-dominant swipe of meaningful distance so
        // vertical scrolls and small finger jitters don't switch tabs.
        if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

        const tabs = document.querySelectorAll('#mp-game-board .mp-tab');
        if (tabs.length < 2) return;

        let currentIdx = 0;
        if (_activeMPTab.startsWith('other-')) {
            currentIdx = parseInt(_activeMPTab.substring(6), 10) + 1;
        }

        // Swipe right (dx > 0) → previous tab; swipe left → next tab.
        const nextIdx = currentIdx + (dx > 0 ? -1 : 1);
        if (nextIdx < 0 || nextIdx >= tabs.length) return;

        const newTab = nextIdx === 0 ? 'mine' : 'other-' + (nextIdx - 1);
        showMPTab(newTab, tabs[nextIdx]);
        haptic('light');
    }, { passive: true });
}

// Preserve all tab state across HTMX polls
document.addEventListener('htmx:afterSettle', () => {
    const board = document.getElementById('mp-game-board');
    if (!board) return;

    // Restore main tab
    if (_activeMPTab !== 'mine' && document.getElementById('mp-tab-' + _activeMPTab)) {
        const tabs = board.querySelectorAll('.mp-tab');
        const idx = _activeMPTab.replace('other-', '');
        const btn = tabs[parseInt(idx, 10) + 1]; // +1 to skip "Your Board"
        if (btn) showMPTab(_activeMPTab, btn);
    }

    // Restore other-player sub-tabs
    for (const idx of Object.keys(_activeOtherTabs)) {
        if (_activeOtherTabs[idx] !== 'scores') {
            const panel = document.getElementById('mp-tab-other-' + idx);
            if (panel) {
                const btn = panel.querySelectorAll('.result-tab')[1];
                if (btn) showOtherPlayerTab(idx, _activeOtherTabs[idx], btn);
            }
        }
    }

    checkInterstitialAd(true);
});

// --- Leaderboard Tabs ---

function showLeaderboardTab(tab, btn) {
    const today = document.getElementById('lb-today');
    const alltime = document.getElementById('lb-alltime');
    if (!today || !alltime) return;

    if (tab === 'today') {
        today.style.display = '';
        alltime.style.display = 'none';
    } else {
        today.style.display = 'none';
        alltime.style.display = '';
    }

    document.querySelectorAll('.leaderboard-tab').forEach(t => t.removeAttribute('data-active'));
    btn.setAttribute('data-active', 'true');
}

// --- Guest Play Flow: reasons modal -> ad modal -> start game ---

// Home page: swap the sign-in form in for the pitch, in place. Also
// honours /#sign-in (which /sign-in redirects to) on load.
function showHomeSignIn() {
    const panel = document.getElementById('home-signin-panel');
    if (!panel) return;
    panel.hidden = false;
    const pitch = document.getElementById('home-pitch');
    if (pitch) pitch.hidden = true;
    const links = document.getElementById('home-auth-links');
    if (links) links.hidden = true;
    const user = panel.querySelector('input[name="username"]');
    if (user) user.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
}
window.showHomeSignIn = showHomeSignIn;

function openAppLink(url) {
    let u;
    try { u = new URL(url); } catch (e) { return; }
    if (!/(^|\.)wyldtyle\.com$/.test(u.hostname)) return;
    const target = u.pathname + u.search;
    if (target === location.pathname + location.search) return;
    window.location.href = target;
}


function startGuestFlow() {
    const modal = document.getElementById('why-account-modal');
    if (modal) modal.style.display = '';
}

function cancelGuestFlow() {
    const why = document.getElementById('why-account-modal');
    const ad = document.getElementById('guest-ad-modal');
    if (why) why.style.display = 'none';
    if (ad) ad.style.display = 'none';
}

// Set in localStorage once a guest has started a game on this device.
// The pre-game interstitial is skipped until then: the first game is
// where new players decide whether to stay, and an ad before the first
// turn is the most expensive impression we could show. Repeat guests
// have already seen the account pitch and get the ad from then on.
const GUEST_PLAYED_KEY = 'wt_guest_played';

function isFirstGuestGame() {
    try {
        if (window.localStorage.getItem(GUEST_PLAYED_KEY)) return false;
        window.localStorage.setItem(GUEST_PLAYED_KEY, '1');
        return true;
    } catch (e) {
        // Storage blocked (private mode, etc.): can't tell, so err on the
        // side of not showing an ad before the first turn.
        return true;
    }
}

function continueToGuestAd() {
    const why = document.getElementById('why-account-modal');
    if (why) why.style.display = 'none';

    if (isFirstGuestGame()) {
        startGuestGame();
        return;
    }

    const config = getAdConfig();
    if (!config.showAds) {
        submitGuestGame();
        return;
    }

    // Native: show an AdMob interstitial (same as the in-game ads) and
    // start the game when it closes, instead of the web house ad.
    if (config.isNative) {
        showNativeGuestAd(config);
        return;
    }

    const ad = document.getElementById('guest-ad-modal');
    if (!ad || !showHouseAd(ad, 'guest_first')) submitGuestGame();
}

// Start the guest's game with no ad and no upgrade pitch (nothing was
// shown to be tired of).
function submitGuestGame() {
    const form = document.getElementById('guest-play-form');
    if (form) form.submit();
}

// Shows the guest pre-game interstitial via AdMob on native, then starts
// the game on dismiss. Falls back to starting immediately if AdMob isn't
// available, has no unit ID, or fails — so the guest is never blocked.
function showNativeGuestAd(config) {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.AdMob) {
        startGuestGame();
        return;
    }
    const AdMob = Capacitor.Plugins.AdMob;
    const adId = Capacitor.getPlatform() === 'ios'
        ? config.admobInterstitialIOS
        : config.admobInterstitialAndroid;
    if (!adId) {
        startGuestGame();
        return;
    }
    _afterInterstitialDismiss = startGuestGame;
    AdMob.prepareInterstitial({ adId: adId })
        .then(() => { hideStatusBarForAd(); track('ad_shown', { network: 'admob', placement: 'guest_first' }); return AdMob.showInterstitial(); })
        .catch(err => {
            console.warn('AdMob guest interstitial failed', err);
            _afterInterstitialDismiss = null;
            startGuestGame();
        });
}

function startGuestGame() {
    const ad = document.getElementById('guest-ad-modal');
    if (ad) ad.style.display = 'none';
    maybeShowUpgradePitch(() => {
        const form = document.getElementById('guest-play-form');
        if (form) form.submit();
    });
}

// --- Nav Menu ---

function toggleNavMenu() {
    const dropdown = document.getElementById('nav-dropdown');
    if (dropdown) dropdown.classList.toggle('open');
}

// Close dropdown when clicking outside
document.addEventListener('click', e => {
    const menu = document.querySelector('.nav-menu');
    const dropdown = document.getElementById('nav-dropdown');
    if (dropdown && menu && !menu.contains(e.target)) {
        dropdown.classList.remove('open');
    }
});

// --- Event Listeners ---

function setupBoard() {
    // Reset bank selection after any swap
    selectedBankIndex = -1;
    selectedBankLetter = '';
}

// --- Ad Infrastructure ---

const _interstitialShown = {};
const AD_GATE_SECONDS = 5;
// Length of the Wyld Pyle celebration. Keep in sync with the .wyld-pyle-*
// animations in style.css (2.8s) so a finished-game interstitial waits for
// the animation to finish instead of overlapping it.
const WYLD_PYLE_CELEBRATION_MS = 2800;

// "Wyld Tyle!" when a wild is rolled. style.css brings in the big tile, rays
// and stamp; this flicks the tile through a few letters like a slot reel,
// lands it on the wild glyph, then flies it into its real slot in the bank.
// Runs once per rendered overlay; an HTMX swap mid-flight just discards it.
// The server renders the overlay whenever the bank looks freshly rolled,
// which an undo or a reload also produces, so each roll is remembered
// and celebrated only the first time.
const WILD_SEEN_KEY = 'wt_wild_seen';

function wildRollSeen(roll) {
    if (!roll) return false;
    let seen = [];
    try { seen = JSON.parse(window.localStorage.getItem(WILD_SEEN_KEY) || '[]'); } catch (e) { /* storage blocked */ }
    if (!Array.isArray(seen)) seen = [];
    if (seen.indexOf(roll) >= 0) return true;
    seen.push(roll);
    try { window.localStorage.setItem(WILD_SEEN_KEY, JSON.stringify(seen.slice(-20))); } catch (e) { /* storage blocked */ }
    return false;
}

function playWildCelebration() {
    const overlay = document.querySelector('.wild-celebration:not([data-played])');
    if (!overlay) return;
    overlay.dataset.played = '1';
    if (wildRollSeen(overlay.dataset.roll)) {
        overlay.remove();
        return;
    }
    const bankTile = document.querySelector('.bank-tile.wild');
    if (!bankTile || !overlay.animate ||
        (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        overlay.remove();
        return;
    }
    bankTile.classList.add('wild-arriving');
    sfx('wild');
    const big = overlay.querySelector('.wild-celebration-tile');
    const face = big.querySelector('.tile-letter');
    const glyph = face.innerHTML;
    const at = (x, y, rest) => `translate(-50%, -50%) translate(${x}px, ${y}px) ${rest}`;

    const reel = ['E', 'V', 'A', 'T', 'Y', 'R', 'B', 'S', 'M'];
    let t = 0;
    reel.forEach((ch, i) => {
        setTimeout(() => { face.textContent = ch; }, t);
        t += i < 5 ? 48 : 48 + (i - 4) * 24;
    });
    setTimeout(() => {
        face.innerHTML = glyph;
        big.animate([
            { transform: at(0, 0, 'scale(1)') },
            { transform: at(0, 0, 'scale(1.18)') },
            { transform: at(0, 0, 'scale(1)') },
        ], { duration: 260, easing: 'ease-out' });
        const r = big.getBoundingClientRect();
        wildSparks(overlay, r.left + r.width / 2, r.top + r.height / 2, 10, r.width * 0.9, r.width * 0.16);
    }, t);

    // Fly once the stamp has had its moment (it leaves at 1.49s in CSS).
    setTimeout(() => {
        const from = big.getBoundingClientRect();
        const to = bankTile.getBoundingClientRect();
        const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
        const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
        const s = to.width / from.width;
        big.animate([
            { transform: at(0, 0, 'scale(1) rotate(0deg)') },
            { transform: at(dx * 0.35, dy * 0.2 - 36, `scale(${(1 + s) / 2 * 1.08}) rotate(-10deg)`), offset: 0.45 },
            { transform: at(dx, dy, `scale(${s}) rotate(0deg)`) },
        ], { duration: 480, easing: 'cubic-bezier(.45,0,.35,1)', fill: 'forwards' }).finished.then(() => {
            big.remove();
            bankTile.classList.remove('wild-arriving');
            bankTile.animate([{ transform: 'scale(1.3)' }, { transform: 'scale(.92)' }, { transform: 'scale(1)' }],
                { duration: 320, easing: 'ease-out' });
            wildSparks(overlay, to.left + to.width / 2, to.top + to.height / 2, 7, 36, 10);
            haptic('light');
            setTimeout(() => overlay.remove(), 800);
        }).catch(() => {});
    }, 1490);
}

// "Last Call" when a game reaches the trade (style.css animates it). Plays
// once per game, so undoing turn 19 or reloading doesn't replay it, and
// dismisses itself after LAST_CALL_MS or on a tap.
const LAST_CALL_MS = 2400;
let lastCallTimer = 0;

function playLastCall() {
    const overlay = document.querySelector('.last-call:not([data-played])');
    if (!overlay) return;
    overlay.dataset.played = '1';
    const key = 'wt_last_call_' + overlay.dataset.game;
    let seen = false;
    try { seen = sessionStorage.getItem(key) === '1'; sessionStorage.setItem(key, '1'); } catch (e) {}
    if (!seen) sfx('lastcall');
    if (seen || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        overlay.remove();
        return;
    }
    overlay.classList.add('playing');
    haptic('light');
    clearTimeout(lastCallTimer);
    lastCallTimer = setTimeout(dismissLastCall, LAST_CALL_MS);
}

function dismissLastCall() {
    clearTimeout(lastCallTimer);
    const overlay = document.querySelector('.last-call.playing:not(.leaving)');
    if (!overlay) return;
    overlay.classList.add('leaving');
    setTimeout(() => overlay.remove(), 300);
}

function wildSparks(overlay, x, y, n, dist, size) {
    for (let i = 0; i < n; i++) {
        const s = document.createElement('span');
        s.className = 'wild-spark';
        s.style.cssText = `left:${x}px;top:${y}px;width:${size}px;height:${size}px`;
        s.innerHTML = '<svg><use href="#i-wild"/></svg>';
        overlay.appendChild(s);
        const ang = i / n * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
        const d = dist * (0.75 + Math.random() * 0.45);
        const dx = Math.cos(ang) * d, dy = Math.sin(ang) * d;
        s.animate([
            { transform: 'translate(-50%, -50%) scale(.2)', opacity: 1 },
            { transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(1) rotate(${Math.random() * 240 - 120}deg)`, opacity: 1, offset: 0.6 },
            { transform: `translate(-50%, -50%) translate(${dx * 1.2}px, ${dy * 1.2 + 12}px) scale(.3)`, opacity: 0 },
        ], { duration: 700, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' });
    }
}

function armAdGate(button, seconds) {
    if (!button) return;
    const label = button.textContent;
    let remaining = seconds;
    button.disabled = true;
    button.textContent = label + ' ';
    const counter = document.createElement('span');
    counter.className = 'ad-gate-counter';
    counter.textContent = '(' + remaining + ')';
    button.appendChild(counter);
    const interval = setInterval(() => {
        remaining--;
        if (remaining <= 0) {
            clearInterval(interval);
            button.disabled = false;
            button.textContent = label;
        } else {
            counter.textContent = '(' + remaining + ')';
        }
    }, 1000);
}

function getAdConfig() {
    const body = document.body;
    return {
        showAds: body.dataset.showAds === 'true',
        isNative: isCapacitor(),
        platform: isCapacitor() ? 'admob' : 'house',
        admobInterstitialIOS: body.dataset.admobInterstitialIos || '',
        admobInterstitialAndroid: body.dataset.admobInterstitialAndroid || ''
    };
}

// The web shows first-party house ads (partials/house_ad.html) wherever
// the native app shows an AdMob interstitial. Creatives rotate in order
// across showings, alternating advertisers (houseAdRotation), and the
// position survives reloads so a player sees each one before any
// repeats. A new player starts at a random point, so no advertiser
// always goes first.
const HOUSE_AD_KEY = 'wt_house_ad';

// houseAdPosition is the player's place in a rotation of `count`
// creatives, kept under `key`; random the first time. Unless `peek`, it
// moves the player one along for next time.
function houseAdPosition(key, count, peek) {
    let n = NaN;
    try { n = parseInt(window.localStorage.getItem(key), 10); } catch (e) { /* storage blocked */ }
    if (!(n >= 0)) n = Math.floor(Math.random() * count);
    if (!peek) {
        try { window.localStorage.setItem(key, String(n + 1)); } catch (e) { /* storage blocked */ }
    }
    return n % count;
}

// showHouseAd reveals the overlay with the next creative and arms the
// Continue gate. Returns false (and shows nothing) if the overlay has no
// creatives, so callers can carry on as if no ad was due.
function showHouseAd(overlay, placement) {
    const creatives = overlay.querySelectorAll('.house-ad');
    if (!creatives.length) return false;
    const pick = creatives[houseAdPosition(HOUSE_AD_KEY, creatives.length)];
    creatives.forEach(c => { c.hidden = c !== pick; });
    const creative = pick.dataset.creative;
    const brand = pick.dataset.brand;
    pick.onclick = () => track('ad_click', { network: 'house', placement: placement, brand: brand, creative: creative });
    overlay.style.display = '';
    track('ad_shown', { network: 'house', placement: placement, brand: brand, creative: creative });
    armAdGate(overlay.querySelector('.ad-interstitial-close'), AD_GATE_SECONDS);
    return true;
}

// initHouseBanner cycles the leaderboard banner under the header through
// the creatives, a few seconds each, starting one further along on each
// page load (its own count, random at first, so the banner and the
// interstitial don't step on each other). It holds still while the pointer or focus is on it,
// while the tab is hidden, and for anyone who prefers reduced motion. Each
// creative counts as shown once per page, the first time it's on screen;
// CSS hides the banner over game boards on phones, where nothing counts.
const HOUSE_BANNER_KEY = 'wt_house_banner';
const HOUSE_BANNER_MS = 8000;

function initHouseBanner() {
    const banner = document.querySelector('.house-banner');
    if (!banner) return;
    const creatives = Array.from(banner.querySelectorAll('.house-banner-ad'));
    if (!creatives.length) return;
    const offScreen = getComputedStyle(banner).display === 'none';
    let cur = houseAdPosition(HOUSE_BANNER_KEY, creatives.length, offScreen);
    if (offScreen) {
        creatives.forEach((c, i) => { c.hidden = i !== cur; });
        return;
    }

    const counted = new Set();
    const show = i => {
        cur = i;
        creatives.forEach((c, j) => {
            const on = j === i;
            c.hidden = false;
            c.toggleAttribute('data-off', !on);
            c.inert = !on;
            if (on) c.removeAttribute('aria-hidden'); else c.setAttribute('aria-hidden', 'true');
        });
        const creative = creatives[i].dataset.creative;
        if (!counted.has(creative)) {
            counted.add(creative);
            track('ad_shown', { network: 'house', placement: 'banner', brand: creatives[i].dataset.brand, creative: creative });
        }
    };
    creatives.forEach(c => {
        c.onclick = () => track('ad_click', { network: 'house', placement: 'banner', brand: c.dataset.brand, creative: c.dataset.creative });
    });
    show(cur);
    requestAnimationFrame(() => requestAnimationFrame(() => banner.setAttribute('data-ready', '')));

    if (creatives.length < 2 ||
        (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
    let held = false;
    banner.addEventListener('mouseenter', () => { held = true; });
    banner.addEventListener('mouseleave', () => { held = false; });
    banner.addEventListener('focusin', () => { held = true; });
    banner.addEventListener('focusout', () => { held = false; });
    setInterval(() => {
        if (held || document.hidden) return;
        show((cur + 1) % creatives.length);
    }, HOUSE_BANNER_MS);
}

// viaSwap: true when called after an HTMX swap, i.e. the player just
// acted. Game ends and the halfway turn always arrive by swap, so a plain
// page load never shows an interstitial: not a finished game revisited
// from history, not a shared /results page, not the page the app opens
// from a Universal Link.
function checkInterstitialAd(viaSwap) {
    if (!viaSwap) return;
    const config = getAdConfig();
    if (!config.showAds) return;

    // Read state from the rendered partials.
    let gameID;
    let turn = 0;
    let totalTurns = 19;
    let phase;
    let mpStatus;
    let daily = false;
    let runEnded = false;
    const wordGrid = document.getElementById('word-grid');
    if (wordGrid) {
        gameID = wordGrid.dataset.gameId;
        turn = parseInt(wordGrid.dataset.turn || '0', 10);
        totalTurns = parseInt(wordGrid.dataset.totalTurns || '19', 10);
        phase = wordGrid.dataset.phase;
        daily = wordGrid.dataset.daily === '1';
        runEnded = wordGrid.dataset.runEnded === '1';
    }
    const mpBoard = document.getElementById('mp-game-board');
    if (mpBoard) {
        mpStatus = mpBoard.dataset.mpStatus;
        if (!gameID) gameID = mpBoard.dataset.mpGameId;
    }
    if (!gameID) return;

    // Cadence: a daily run shows one interstitial, when the run ends
    // (not per level, never mid-level). Any other game shows one halfway
    // through and one at the end.
    let key = null;
    if (daily) {
        if (phase === 'finished' && runEnded) key = gameID + ':run-ended';
    } else if (mpStatus === 'finished' || phase === 'finished') {
        key = gameID + ':finished';
    } else if (turn > 0 && turn === Math.ceil(totalTurns / 2)) {
        key = gameID + ':halfway';
    }
    if (!key || _interstitialShown[key]) return;
    _interstitialShown[key] = true;

    const showAd = () => {
        if (config.isNative) {
            // AdMob fullscreen interstitial; the dismiss listener registered
            // in initAdMob() runs the upgrade-pitch cadence on close.
            loadAdMobInterstitial(config);
            return;
        }

        const interstitial = document.getElementById('ad-interstitial');
        if (interstitial) showHouseAd(interstitial, 'game');
    };

    // Hold the interstitial while the Wyld Pyle celebration plays and an
    // achievement toast has its moment, so they don't overlap.
    const wait = Math.max(celebrationDelay(), _achHoldUntil - Date.now());
    if (wait > 0) {
        setTimeout(showAd, wait);
    } else {
        showAd();
    }
}

// celebrationDelay is how long the Wyld Pyle celebration on screen has
// left: all of it, since it starts with the swap that brings it.
// Reduced-motion users don't see the animation.
function celebrationDelay() {
    const celebrating = document.querySelector('.wyld-pyle-celebration') &&
        !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    return celebrating ? WYLD_PYLE_CELEBRATION_MS : 0;
}

// --- Achievements ---
//
// A finished game can bring a toast (partials/achievements.html): what it
// unlocked, or for a guest, what it would have. It appears once any Wyld
// Pyle celebration is over, stays ACH_TOAST_MS, and the interstitial waits
// ACH_AD_HOLD_MS after it appears (the toast sits above the ad after that).
const ACH_TOAST_MS = 6000;
const ACH_AD_HOLD_MS = 2500;
let _achHoldUntil = 0;

function showAchievementToast() {
    const toast = document.querySelector('[data-achievement-toast][hidden]');
    if (!toast) return;
    // Off the board, so a poll or the next swap can't take it mid-show.
    document.body.appendChild(toast);
    const lead = celebrationDelay() + 400;
    _achHoldUntil = Date.now() + lead + ACH_AD_HOLD_MS;
    setTimeout(() => {
        toast.hidden = false;
        sfx('unlock');
        haptic('medium');
        const guest = !!toast.querySelector('a[href="/sign-up"]');
        track('achievement_toast', { count: toast.querySelectorAll('.ach-toast-item').length, guest: guest });
        const hide = () => {
            toast.classList.add('ach-toast-out');
            setTimeout(() => toast.remove(), 300);
        };
        const timer = setTimeout(hide, ACH_TOAST_MS);
        toast.querySelector('.ach-toast-close').addEventListener('click', () => { clearTimeout(timer); hide(); });
    }, lead);
}

// /dev/achievement (local only) shows a game's end on page load, but
// browsers keep a page silent until it's clicked. So the preview holds the
// celebration and toast behind a button, then plays them with sound.
function initDevAchievementPreview() {
    if (location.pathname !== '/dev/achievement') return;
    const cel = document.querySelector('.wyld-pyle-celebration');
    const parent = cel && cel.parentNode;
    const next = cel && cel.nextSibling;
    if (cel) cel.remove();
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-primary dev-preview-play';
    btn.textContent = 'Play the finish, with sound';
    document.body.appendChild(btn);
    btn.addEventListener('click', () => {
        btn.remove();
        if (cel) parent.insertBefore(cel, next); // reinserting restarts its CSS animation
        sfx(cel ? 'pyle' : 'finish');
        showAchievementToast();
    });
}

// The one-time summary for accounts from before achievements. Its links
// may point at the page it's on (the profile's tabs), so every way out
// closes it.
function initAchievementIntro() {
    const intro = document.querySelector('[data-achievement-intro]');
    if (!intro) return;
    const close = () => intro.remove();
    intro.querySelectorAll('[data-close]').forEach(el => el.addEventListener('click', close));
    intro.addEventListener('click', e => { if (e.target === intro) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
    track('achievement_intro', { earned: intro.querySelectorAll('.ach-intro-grid li').length });
}

function loadAdMobInterstitial(config) {
    if (!isCapacitor() || !Capacitor.Plugins || !Capacitor.Plugins.AdMob) return;
    const AdMob = Capacitor.Plugins.AdMob;

    const platform = Capacitor.getPlatform();
    const adId = platform === 'ios' ? config.admobInterstitialIOS : config.admobInterstitialAndroid;
    if (!adId) {
        // No unit ID configured; bail and run the pitch cadence so the
        // user's flow isn't blocked.
        maybeShowUpgradePitch();
        return;
    }

    AdMob.prepareInterstitial({ adId: adId })
        .then(() => { hideStatusBarForAd(); track('ad_shown', { network: 'admob', placement: 'game' }); return AdMob.showInterstitial(); })
        .catch(err => {
            console.warn('AdMob interstitial failed', err);
            maybeShowUpgradePitch();
        });
}

function closeInterstitialAd() {
    const interstitial = document.getElementById('ad-interstitial');
    if (interstitial) interstitial.style.display = 'none';
    maybeShowUpgradePitch();
}

// --- Upgrade Pitch Modal ---
//
// Fires every other ad-close to nudge users toward the ad-free upgrade.
// `next` is an optional callback invoked once the pitch is dismissed
// (or immediately if no pitch is shown this time).
let _adCloseCount = 0;
let _pitchPendingCallback = null;

function maybeShowUpgradePitch(next) {
    _adCloseCount++;
    const pitch = document.getElementById('upgrade-pitch-modal');
    const shouldPitch = pitch && (_adCloseCount % 2 === 0);
    if (shouldPitch) {
        _pitchPendingCallback = next || null;
        pitch.style.display = '';
        return;
    }
    if (next) next();
}

function closeUpgradePitch() {
    const pitch = document.getElementById('upgrade-pitch-modal');
    if (pitch) pitch.style.display = 'none';
    const fn = _pitchPendingCallback;
    _pitchPendingCallback = null;
    if (fn) fn();
}

// Run on initial page load and after every HTMX swap
// Keyboard activation for non-native interactive elements (the board slots
// and dice are role="button" divs). Enter/Space fires their click handler,
// matching native <button> behavior for keyboard and switch-control users.
// VoiceOver and Voice Control already synthesize a click, so this only adds
// hardware-keyboard support.
document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = e.target;
    if (el instanceof HTMLElement && el.getAttribute('role') === 'button' && el.hasAttribute('onclick')) {
        e.preventDefault();
        el.click();
    }
});

// Our own "get the app" banner (iPhone browsers that don't draw Safari's
// Smart App Banner). Dismissing hides it for 30 days.
function dismissInstallBanner() {
    const el = document.getElementById('install-banner');
    if (el) el.hidden = true;
    try {
        document.cookie = 'wt_appbanner=1; path=/; max-age=2592000; samesite=lax';
    } catch (e) { /* cookies blocked: it just comes back next visit */ }
}
window.dismissInstallBanner = dismissInstallBanner;
window.dismissLastCall = dismissLastCall;

function openAppLink(url) {
    let u;
    try { u = new URL(url); } catch (e) { return; }
    if (!/(^|\.)wyldtyle\.com$/.test(u.hostname)) return;
    const target = u.pathname + u.search;
    if (target === location.pathname + location.search) return;
    window.location.href = target;
}

function initAppLinks() {
    const App = Capacitor.Plugins.App;
    if (!App) return;
    // Warm start: the OS hands the URL to the running app.
    App.addListener('appUrlOpen', ev => { if (ev && ev.url) openAppLink(ev.url); });
    // Cold start: the launch URL is available to every page for the life
    // of the process, so act on it once. Without the marker, each later
    // page load (tapping Home, Sign In, anything) bounced straight back
    // to the linked page.
    if (App.getLaunchUrl) {
        App.getLaunchUrl().then(res => {
            if (!res || !res.url) return;
            let handled = null;
            try { handled = sessionStorage.getItem('wt_launch_url'); } catch (e) { /* storage blocked */ }
            if (handled === res.url) return;
            try { sessionStorage.setItem('wt_launch_url', res.url); } catch (e) { /* ignore */ }
            openAppLink(res.url);
        }).catch(() => {});
    }
}

// The daily board is keyed to the player's local date; tell the server
// the timezone once so it can work that out on every request.
function syncTimezoneCookie() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!tz) return;
        const m = document.cookie.match(/(?:^|; )wt_tz=([^;]*)/);
        if (m && decodeURIComponent(m[1]) === tz) return;
        document.cookie = 'wt_tz=' + encodeURIComponent(tz) + '; path=/; max-age=31536000; samesite=lax';
    } catch (e) { /* cookies blocked: server falls back to UTC */ }
}

// Share a finished game: the system share sheet where there is one,
// otherwise copy to the clipboard and say so on the button.
async function shareResult(btn) {
    const text = btn.dataset.shareText || '';
    const url = btn.dataset.shareUrl || location.href;
    const full = text + '\n' + url;
    if (navigator.share) {
        try {
            await navigator.share({ text: full });
            track('share', { method: 'native', content_type: btn.dataset.shareUrl ? 'result' : 'text' });
            return;
        } catch (e) {
            if (e && e.name === 'AbortError') return; // user closed the sheet
        }
    }
    try {
        await navigator.clipboard.writeText(full);
        track('share', { method: 'clipboard', content_type: 'result' });
        const label = btn.textContent;
        btn.textContent = 'Copied!';
        btn.disabled = true;
        setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 1800);
    } catch (e) {
        window.prompt('Copy your result:', full);
    }
}
window.shareResult = shareResult;

// A mailto: link does nothing at all in a browser with no mail app set up
// (desktop Chrome for most webmail users). When the page hasn't lost focus
// shortly after the click, nothing opened: show the address on the link,
// and copy it where the browser allows.
document.addEventListener('click', e => {
    const link = e.target.closest && e.target.closest('a[href^="mailto:"]');
    if (!link || isCapacitor()) return;
    const address = decodeURIComponent(link.getAttribute('href').slice(7).split('?')[0]);
    setTimeout(() => {
        if (!document.hasFocus() || document.visibilityState !== 'visible') return;
        if (link.dataset.fallback) return;
        const label = [...link.childNodes].reverse().find(n => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
        const text = label ? label.textContent : null;
        const show = s => { if (label) label.textContent = s; };
        link.dataset.fallback = '1';
        if (label && label.textContent.trim() !== address) show(address);
        if (navigator.clipboard) {
            navigator.clipboard.writeText(address).then(() => { if (link.dataset.fallback) show('Copied ' + address); }).catch(() => {});
        }
        setTimeout(() => { if (text !== null) show(text); delete link.dataset.fallback; }, 4000);
    }, 800);
});

document.addEventListener('DOMContentLoaded', () => {
    syncTimezoneCookie();
    if (location.hash === '#sign-in') showHomeSignIn();
    setupBoard();
    playWildCelebration();
    initDevAchievementPreview();
    playLastCall();
    initTutorial();
    initCapacitor();
    syncThemeChrome();
    initWebPush();
    initPushNudge();
    initMPSwipe();
    initAnalytics();
    initHouseBanner();
    initSound();
    initAchievementIntro();
});
document.addEventListener('htmx:beforeSwap', () => {
    sfxBefore = sfxBoardState();
});
document.addEventListener('htmx:afterSwap', () => {
    sfxAfterSwap();
    setupBoard();
    tutorialSync();
    playWildCelebration();
    playLastCall();
    flushGAEvents();
    showAchievementToast();
    checkInterstitialAd(true);
});

// Haptic feedback for HTMX-triggered actions (roll type buttons, etc.)
document.addEventListener('htmx:beforeRequest', evt => {
    const el = evt.detail.elt;
    if (el && el.classList && el.classList.contains('roll-btn')) {
        haptic('light');
        sfx('roll');
    }
    const path = evt.detail.requestConfig && evt.detail.requestConfig.path;
    if (path && /\/undo(-turn)?$/.test(path)) sfx('undo');
});

// --- Live views ---
//
// Parts of a page that follow what other players do poll the server:
// the multiplayer board (.mp-poll, every 15s) and the home page's Up next
// list (.live-poll, every 60s). Each poll carries a version, and an
// unchanged view answers 204, so a poll is cheap. To keep costs down,
// polls are skipped while the page is hidden. And so nobody stares at
// "Waiting for Sam" after the "your turn" notification, everything live
// on the page is refreshed at once when a push arrives, when the app
// returns to the foreground, or when the page becomes visible.
const LIVE_POLLS = '.mp-poll[hx-get], .live-poll[hx-get]';

document.addEventListener('htmx:beforeRequest', evt => {
    if (document.visibilityState !== 'hidden') return;
    const el = evt.detail.elt;
    if (el && el.matches && el.matches(LIVE_POLLS)) {
        evt.preventDefault();
    }
});

let liveRefreshAt = 0;
function refreshLiveViews() {
    // Resume, visibility and a push often land together; one refresh does.
    const now = Date.now();
    if (now - liveRefreshAt < 1000) return;
    liveRefreshAt = now;
    document.querySelectorAll(LIVE_POLLS).forEach(el => {
        htmx.ajax('GET', el.getAttribute('hx-get'), {
            target: el.getAttribute('hx-target') || '#mp-game-board',
            swap: el.getAttribute('hx-swap') || 'outerHTML',
        });
    });
}
window.refreshLiveViews = refreshLiveViews;

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshLiveViews();
});

// Web push: the service worker tells open pages a notification arrived.
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', evt => {
        if (evt.data && evt.data.type === 'wyldtyle-push') refreshLiveViews();
    });
}
